import React, { useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import styled from 'styled-components';
import { utils } from '@makeship/core';
import { Paragraph } from '../../ui/typography';
import { useStore } from '../../store';
import {
  hideCart,
  quantityModifier,
  clientDecreaseQuantity,
  clientIncreaseQuantity,
  removeProduct,
  clientRemoveProduct,
} from '../../store/store.actions';
import { parseMetafields, hasProductTag, isSizedApparelProduct } from '../../utils/product';

import Stepper from '../Stepper';
import { getPricePerQuantity } from '../../utils/cart';
import { analyticsTrackAddToCart, analyticsTrackRemoveFromCart } from '../../utils/analytics';
import { CartEventLocation, ProductTag } from '../../types/common';
import SpamFlaggingContext from '../../context/spamFlagging';
import { getFormattedAmount } from '../../utils/accounting';

const CartProductWrapper = styled.div<{ first: boolean }>`
  display: flex;
  flex-direction: row;
  padding: 0;
  padding-top: 16px;

  ${({ first, theme }) =>
    !first && `border-top: 1px solid ${utils.hexToRGBA(theme.colors.neutral7, theme.alpha.light)};`}

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    padding: 8px 0;
  }
`;

const ProductImageWrapper = styled.div`
  width: 110px;
  height: 110px;
`;

const CartProductInfo = styled.div`
  margin-left: 8px;
  width: 100%;
`;

const CartProductRow = styled.div<{ shouldShowVariantTitle: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  ${({ shouldShowVariantTitle }) => !shouldShowVariantTitle && `margin-bottom: 8px;`}
`;

const CartProductLink = styled.a`
  display: block;
  cursor: pointer;
`;

const CartProductPriceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
`;

const CustomStepperWrapper = styled.div`
  margin-left: auto;
`;

type CartProductProps = {
  lineItem: Shopify.CartLine;
  index: number;
};

const CartProduct: React.FC<CartProductProps> = ({ lineItem, index }: CartProductProps) => {
  const { state, dispatch } = useStore();
  const { incrementSpamCounter, isSpam } = useContext(SpamFlaggingContext);
  const metafields = parseMetafields(lineItem.merchandise?.product.metafields);
  const isLimited = lineItem.merchandise
    ? hasProductTag(lineItem.merchandise?.product?.tags, ProductTag.Limited)
    : false;
  const [totalBeforeDiscounts, setTotalBeforeDiscounts] = useState(0.0);
  const [totalAfterDiscounts, setTotalAfterDiscounts] = useState(0.0);
  const shouldShowVariantTitle = isSizedApparelProduct(lineItem.merchandise?.product || '');

  useEffect(() => {
    const appliedDiscountsAllocations = lineItem.discountAllocations.reduce(
      (total, discount) =>
        total + (!Number.isNaN(discount.discountedAmount.amount) ? parseFloat(discount.discountedAmount.amount) : 0),
      0,
    );
    const compareAtPriceDiscount = lineItem.cost.compareAtAmountPerQuantity
      ? lineItem.cost.compareAtAmountPerQuantity?.amount - lineItem.cost.amountPerQuantity.amount
      : 0;

    const tempTotalBeforeDiscounts = lineItem.cost.compareAtAmountPerQuantity
      ? lineItem.cost.compareAtAmountPerQuantity?.amount * lineItem.quantity
      : lineItem.cost.amountPerQuantity.amount * lineItem.quantity;

    const tempTotalAfterDiscounts = lineItem.cost.compareAtAmountPerQuantity
      ? (lineItem.cost.compareAtAmountPerQuantity?.amount - compareAtPriceDiscount) * lineItem.quantity -
        appliedDiscountsAllocations
      : lineItem.cost.amountPerQuantity.amount * lineItem.quantity - appliedDiscountsAllocations;

    setTotalBeforeDiscounts(tempTotalBeforeDiscounts || 0);
    setTotalAfterDiscounts(tempTotalAfterDiscounts || 0);
  }, [lineItem]);

  // Auto-remove items from cart when inventory becomes zero
  useEffect(() => {
    const isAvailableForSale = lineItem.merchandise?.availableForSale;

    // If the variant is not available for sale, remove it from the cart
    if (!isAvailableForSale) {
      dispatch(clientRemoveProduct(lineItem, state.cart));
      removeProduct(lineItem, state.cart).then((action) => dispatch(action));
    }
  }, [lineItem.merchandise?.availableForSale, lineItem, state.cart, dispatch]);

  const isLimitedQtyOutOfStock = () => {
    if (!isLimited) return false;
    if (metafields.inventory) {
      return metafields.inventory - lineItem.quantity <= 0;
    }
  };

  const isOutOfStock = isLimitedQtyOutOfStock();

  const renderItemPrice = () =>
    totalBeforeDiscounts > totalAfterDiscounts || lineItem.cost.compareAtAmountPerQuantity ? (
      <CartProductPriceWrapper>
        <Paragraph color="neutral-300" className="line-through">
          ${getFormattedAmount(totalBeforeDiscounts.toString())}
        </Paragraph>
        <div className="ml-[10px]">
          <Paragraph color="red-600">${getFormattedAmount(totalAfterDiscounts.toString())}</Paragraph>
        </div>
        <CustomStepperWrapper>
          <Stepper
            value={lineItem.quantity}
            increase={() => {
              if (!isSpam && lineItem.merchandise) {
                const { product } = lineItem.merchandise;
                incrementSpamCounter();
                analyticsTrackAddToCart(
                  product,
                  1,
                  state.user,
                  CartEventLocation.CartPopout,
                  lineItem.merchandise,
                  metafields.campaignID,
                );
              }
              dispatch(clientIncreaseQuantity(lineItem, state.cart));
              quantityModifier(lineItem, state.cart).then((action) => dispatch(action));
            }}
            decrease={() => {
              analyticsTrackRemoveFromCart(lineItem, state.user, CartEventLocation.CartPopout);
              dispatch(clientDecreaseQuantity(lineItem, state.cart));
              quantityModifier(lineItem, state.cart).then((action) => dispatch(action));
            }}
            isLimited={isLimited}
            isLimitedQtyOutOfStock={isOutOfStock}
          />
        </CustomStepperWrapper>
      </CartProductPriceWrapper>
    ) : (
      <CartProductPriceWrapper>
        <Paragraph>${getPricePerQuantity(lineItem.cost.amountPerQuantity.amount, lineItem.quantity)}</Paragraph>
        <CustomStepperWrapper>
          <Stepper
            value={lineItem.quantity}
            increase={() => {
              if (!isSpam && lineItem.merchandise) {
                const { product } = lineItem.merchandise;
                incrementSpamCounter();
                analyticsTrackAddToCart(
                  product,
                  1,
                  state.user,
                  CartEventLocation.CartPopout,
                  lineItem.merchandise,
                  metafields.campaignID,
                );
              }
              dispatch(clientIncreaseQuantity(lineItem, state.cart));
              quantityModifier(lineItem, state.cart).then((action) => dispatch(action));
            }}
            decrease={() => {
              analyticsTrackRemoveFromCart(lineItem, state.user, CartEventLocation.CartPopout);
              dispatch(clientDecreaseQuantity(lineItem, state.cart));
              quantityModifier(lineItem, state.cart).then((action) => dispatch(action));
            }}
            isLimited={isLimited}
            isLimitedQtyOutOfStock={isOutOfStock}
          />
        </CustomStepperWrapper>
      </CartProductPriceWrapper>
    );

  return state.cart.open ? (
    <CartProductWrapper first={!index}>
      <ProductImageWrapper>
        <Image
          src={lineItem.merchandise?.image?.transformedSrc || '/assets/default_product_asset.gif'}
          alt={lineItem.merchandise?.image?.altText || 'Cart product image'}
          width={110}
          height={110}
          quality={90}
          unoptimized
        />
      </ProductImageWrapper>
      <CartProductInfo>
        <CartProductRow shouldShowVariantTitle={shouldShowVariantTitle}>
          <Link href={`/products/${lineItem.merchandise?.product.handle}`} passHref legacyBehavior>
            <CartProductLink onClick={() => dispatch(hideCart())}>
              <Paragraph>{lineItem.merchandise.product.title}</Paragraph>
            </CartProductLink>
          </Link>
        </CartProductRow>
        {shouldShowVariantTitle && <Paragraph>Size {lineItem.merchandise?.title}</Paragraph>}
        {renderItemPrice()}
      </CartProductInfo>
    </CartProductWrapper>
  ) : (
    <></>
  );
};

export default CartProduct;
